import React, { useState, useEffect } from 'react';
import { Search, User, Crown, Star, Eye, X } from 'lucide-react';
import { customerAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { format } from 'date-fns';

const StaffCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerTransactions, setCustomerTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchCustomers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  const fetchCustomers = async () => {
    try {
      const response = await customerAPI.getAll({ search, limit: 50 });
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setLoadingTransactions(true);
    try {
      const response = await customerAPI.getTransactions(customer.id, 10);
      setCustomerTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const getTierColor = (tier) => ({
    Bronze: 'tier-bronze',
    Silver: 'tier-silver',
    Gold: 'tier-gold',
    Platinum: 'tier-platinum',
  })[tier] || 'tier-bronze';

  if (loading) {
    return (
      <div className="space-y-6 pt-12 lg:pt-0">
        <div className="skeleton h-12 rounded-xl" />
        {[1,2,3,4,5].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-12 lg:pt-0 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl text-[#4A154B]">Customers</h1>
        <p className="text-gray-600">View and manage customer information</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, or phone..."
          className="pl-12"
          data-testid="customer-search-input"
        />
      </div>

      {/* Customers List */}
      <div className="space-y-3">
        {customers.map((customer) => (
          <div
            key={customer.id}
            className="glass rounded-xl p-4 flex items-center justify-between card-hover cursor-pointer"
            onClick={() => viewCustomer(customer)}
            data-testid={`customer-row-${customer.id}`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F5E6D3] flex items-center justify-center">
                <User className="w-6 h-6 text-[#C9A689]" />
              </div>
              <div>
                <h4 className="font-medium text-[#4A154B]">{customer.name || 'Guest'}</h4>
                <p className="text-sm text-gray-500">{customer.email}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="flex items-center gap-1 text-[#C9A689] font-medium">
                  <Star className="w-4 h-4" />
                  {customer.current_points}
                </p>
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${getTierColor(customer.tier)}`}>
                  {customer.tier}
                </span>
              </div>
              <Eye className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        ))}

        {customers.length === 0 && (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No customers found</p>
          </div>
        )}
      </div>

      {/* Customer Detail Modal */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#4A154B]">Customer Details</DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-[#F5E6D3] flex items-center justify-center">
                  <User className="w-8 h-8 text-[#C9A689]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-serif text-xl text-[#4A154B]">{selectedCustomer.name || 'Guest'}</h3>
                  <p className="text-gray-500">{selectedCustomer.email}</p>
                  {selectedCustomer.phone && <p className="text-gray-500 text-sm">{selectedCustomer.phone}</p>}
                  
                  <div className="flex items-center gap-3 mt-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getTierColor(selectedCustomer.tier)}`}>
                      <Crown className="w-4 h-4 inline mr-1" />
                      {selectedCustomer.tier}
                    </span>
                  </div>
                </div>
              </div>

              {/* Points Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#F5E6D3] rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-600">Current Points</p>
                  <p className="font-serif text-2xl text-[#4A154B]">{selectedCustomer.current_points}</p>
                </div>
                <div className="bg-[#F5E6D3] rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-600">Lifetime Points</p>
                  <p className="font-serif text-2xl text-[#4A154B]">{selectedCustomer.lifetime_points}</p>
                </div>
              </div>

              {/* Recent Transactions */}
              <div>
                <h4 className="font-medium text-[#4A154B] mb-3">Recent Activity</h4>
                {loadingTransactions ? (
                  <div className="space-y-2">
                    {[1,2,3].map(i => <div key={i} className="skeleton h-12 rounded-lg" />)}
                  </div>
                ) : customerTransactions.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {customerTransactions.map((t) => (
                      <div key={t.id} className="flex items-center justify-between py-2 border-b border-[#F5E6D3] last:border-0">
                        <div>
                          <p className="text-sm font-medium text-[#4A154B]">
                            {t.service_name || t.reward_name || 'Transaction'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {format(new Date(t.created_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                        <span className={`font-semibold ${t.type === 'earned' ? 'text-[#8FBC8F]' : 'text-[#C74545]'}`}>
                          {t.type === 'earned' ? '+' : ''}{t.points}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">No transactions yet</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffCustomers;
